import { route } from "quasar/wrappers";
import {
  createRouter,
  createMemoryHistory,
  createWebHistory,
  createWebHashHistory,
} from "vue-router";
import { Loading, QSpinnerBars, QSpinnerIos, Screen } from "quasar";
import { useLogementsStore } from "stores/useLogementsStore";

import routes from "./routes.js";

const CHUNK_LOAD_FAILED_RETRY_KEY = "chunk_load_failed_retry";

function isChunkLoadFailed(error) {
  const message = error?.message || String(error);
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Loading chunk") ||
    message.includes("Loading CSS chunk") ||
    message.includes("ChunkLoadError")
  );
}

function handleChunkLoadError() {
  const hasRetried = sessionStorage.getItem(CHUNK_LOAD_FAILED_RETRY_KEY);

  if (!hasRetried) {
    sessionStorage.setItem(CHUNK_LOAD_FAILED_RETRY_KEY, "true");
    window.location.reload();
    return true;
  }

  sessionStorage.removeItem(CHUNK_LOAD_FAILED_RETRY_KEY);
  return false;
}

export default route(function () {
  const createHistory = process.env.SERVER
    ? createMemoryHistory
    : process.env.VUE_ROUTER_MODE === "history"
    ? createWebHistory
    : createWebHashHistory;

  const Router = createRouter({
    scrollBehavior: (to, from, savedPosition) => {
      // Si on a une position sauvegardée par le navigateur (back/forward)
      if (savedPosition) {
        return savedPosition;
      }

      // Vérifier si on revient à l'accueil avec une position récente
      if (to.path === "/" || to.path === "/mobile") {
        const scrollPosition = sessionStorage.getItem("scrollPosition");
        const scrollTime = sessionStorage.getItem("scrollTime");

        // Si la position est récente (< 5 min)
        if (
          typeof window !== "undefined" &&
          scrollPosition &&
          scrollTime &&
          Date.now() - parseInt(scrollTime) < 300000
        ) {
          // Attendre que le DOM soit complètement rendu avec requestAnimationFrame
          return new Promise((resolve) => {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                setTimeout(() => {
                  const targetScroll = parseInt(scrollPosition);

                  // Double vérification avec window.scrollTo pour être sûr
                  window.scrollTo({
                    top: targetScroll,
                    left: 0,
                    behavior: "auto",
                  });

                  resolve({
                    left: 0,
                    top: targetScroll,
                    behavior: "auto",
                  });
                }, 800);
              });
            });
          });
        }
      }

      // Par défaut, scroller vers le haut
      return { left: 0, top: 0 };
    },
    routes,
    history: createHistory(process.env.VUE_ROUTER_BASE),
  });

  // Sauvegarder la position du scroll et le filtre actif avant de quitter la page (mobile uniquement)
  Router.beforeEach((to, from, next) => {
    // Sauvegarder la position actuelle si on quitte l'accueil UNIQUEMENT pour mobile (router.push)
    if (
      typeof window !== "undefined" &&
      (from.path === "/" || from.path === "/mobile") &&
      to.path !== from.path &&
      to.path.startsWith("/logement/")
    ) {
      const scrollPos = window.scrollY || window.pageYOffset;
      sessionStorage.setItem("scrollPosition", scrollPos.toString());
      sessionStorage.setItem("scrollTime", Date.now().toString());

      // Sauvegarder aussi l'état des filtres
      try {
        const logementsStore = useLogementsStore();
        const filterState = {
          selectedLogementTypeId: logementsStore.selectedLogementTypeId || 0,
        };
        sessionStorage.setItem("filterState", JSON.stringify(filterState));
      } catch (error) {
        // Erreur silencieuse
      }
    }

    next();
  });

  Router.afterEach((to, from) => {
    // Nettoyer les données de scroll et filtres après restauration
    if (
      (to.path === "/" || to.path === "/mobile") &&
      from.path?.startsWith("/logement/")
    ) {
      const scrollPosition = sessionStorage.getItem("scrollPosition");
      const scrollTime = sessionStorage.getItem("scrollTime");

      if (
        scrollPosition &&
        scrollTime &&
        Date.now() - parseInt(scrollTime) < 300000
      ) {
        // Nettoyer après un délai pour laisser le scroll et les filtres se restaurer
        setTimeout(() => {
          sessionStorage.removeItem("scrollPosition");
          sessionStorage.removeItem("scrollTime");
          sessionStorage.removeItem("filterState");
        }, 1500);
      }
    }

    sessionStorage.removeItem(CHUNK_LOAD_FAILED_RETRY_KEY);
  });

  Router.onError((error) => {
    if (isChunkLoadFailed(error)) {
      handleChunkLoadError();
    }
  });

  return Router;
});
